'use client';

import { motion, useReducedMotion } from 'framer-motion';
import { PortableText } from '@portabletext/react';
import { stegaClean } from '@sanity/client/stega';

import type { NewPricingCopy } from '@/components/pageComponents/newPricing/copy';
import type { PagePatrickAffiliateSchema } from '@/sanity/pages/PagePatrickAffiliateTemplate';
import { MeasuredHighlight, measuredRichText } from './richtext';

const ease = [0.25, 0.1, 0.25, 1] as const;

/**
 * Heading type as the other sections set it: Geist Medium 36 / 64px, 1.15,
 * -0.05em; highlighted words tracked -0.03em with a single space before them.
 * The frame draws this wash 4.4px above the line centre on desktop and ends
 * it flush with the period, unlike the other headings.
 */
const HEADING_CLASS =
  'richtext richtext-center prose-headings:text-black prose-headings:font-geist prose-headings:font-[500] prose-headings:leading-[1.15] prose-headings:text-[4.5rem] prose-headings:tracking-[-0.225rem] lg:prose-headings:text-[8rem] lg:prose-headings:tracking-[-0.4rem] [--hl-tracking:-0.03em] [--hl-ml:0.067em] [--hl-dy:-0.04rem] lg:[--hl-dy:-0.55rem] lg:[--hl-pr:0.02em]';

/**
 * The pricing section's eyebrow / title / sub as this page draws them — Figma
 * 8841:2386 (desktop) / 8811:4021 (mobile). The shared section's own header is
 * a different cut (56px title, flat mint block behind the whole second line),
 * so the Patrick page hands this in through `renderHero`.
 *
 * Copy comes from the page document (`pricingChip` / `pricingHeading` /
 * `pricingSubtitle`) so editors control the highlighted words the same way as
 * in every other section; each field falls back to the copy bundle the
 * section resolved, Sanity overrides included. In that fallback the design
 * highlights only the last words of the second line ("Your Trading."), while
 * the bundle keeps the line as one accent string — the first word is split off
 * and set plain.
 */
export default function PricingHeader({
  data,
  copy,
}: {
  data: PagePatrickAffiliateSchema;
  copy: NewPricingCopy;
}) {
  const prefersReducedMotion = useReducedMotion();

  const chip = data?.pricingChip || copy.heroEyebrow;
  // The design breaks the subtitle before its second sentence at both widths.
  const subtitle =
    data?.pricingSubtitle || stegaClean(copy.heroSub).replace(/\.\s+/, '.\n');
  const heading = data?.pricingHeading;

  // Split a cleaned copy: in draft mode stega appends zero-width characters
  // that trim() would cut in half.
  const [lead, ...rest] = stegaClean(copy.heroTitleAccent).trim().split(' ');
  const accent = rest.join(' ');

  return (
    <motion.div
      initial={prefersReducedMotion ? false : { opacity: 0, y: 24 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, amount: 0.4 }}
      transition={{ duration: 0.7, ease }}
      className="flex flex-col items-center text-center gap-[2.5rem]"
    >
      <div className="flex flex-col items-center gap-[0.625rem]">
        {chip && (
          <span className="font-blacker italic text-black text-[2rem] leading-[1.5]">
            {chip}
          </span>
        )}
        {heading?.length ? (
          <div className={HEADING_CLASS}>
            <PortableText value={heading} components={measuredRichText} />
          </div>
        ) : (
          <div className={HEADING_CLASS}>
            <h2>{copy.heroTitleLine1}</h2>
            <h2>
              {accent ? (
                <>
                  {lead}{' '}
                  <MeasuredHighlight tone="light">{accent}</MeasuredHighlight>
                </>
              ) : (
                <MeasuredHighlight tone="light">{lead}</MeasuredHighlight>
              )}
            </h2>
          </div>
        )}
      </div>
      {subtitle && (
        <p className="m-0 whitespace-pre-line font-geist font-[400] text-black/70 text-[2rem] leading-[1.33] w-[46rem] max-w-full lg:text-[2.25rem] lg:w-[51.875rem]">
          {subtitle}
        </p>
      )}
    </motion.div>
  );
}
